/**
 * AdminPage - Phoenix Premium Admin Dashboard
 *
 * Admin panel for analysis overview, user management, and system configuration.
 * Brand color: #00468C (Phoenix blue)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import {
  getAllUsers,
  getPendingUsers,
  approveUser,
  rejectUser,
  deleteUserDocument,
  updateUserCredits,
  UserDocument
} from '../../services/userService';
import { notifyApproval, listAllFirebaseUsers, deleteFirebaseUser, FirebaseAuthUser } from '../../services/api';

// Types
interface AnalysisRecord {
  task_id: string;
  email: string;
  date: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  election_type: string;
  municipality: string;
}

interface SystemConfig {
  admin_emails: string[];
  email_enabled: boolean;
  version: string;
}

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const CoinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v12"/>
    <path d="M8 10h8"/>
    <path d="M8 14h8"/>
  </svg>
);

type TabType = 'analyses' | 'users' | 'config';

const AdminPage: React.FC = () => {
  const { user, signOut, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [analyses, setAnalyses] = useState<AnalysisRecord[]>([]);
  const [users, setUsers] = useState<UserDocument[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserDocument[]>([]);
  const [firebaseUsers, setFirebaseUsers] = useState<FirebaseAuthUser[]>([]);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showFirebaseUsers, setShowFirebaseUsers] = useState(false);
  const [editingCredits, setEditingCredits] = useState<string | null>(null);
  const [creditInputValue, setCreditInputValue] = useState<string>('');

  // Redirect if not admin (only after auth is loaded)
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/filter');
    }
  }, [isAdmin, authLoading, navigate]);

  // Fetch users from Firestore
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [allUsersData, pendingUsersData] = await Promise.all([
        getAllUsers(),
        getPendingUsers()
      ]);
      setUsers(allUsersData);
      setPendingUsers(pendingUsersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Firebase Auth users (all registered users, including those without Firestore docs)
  const fetchFirebaseUsers = async () => {
    setLoading(true);
    try {
      const fbUsers = await listAllFirebaseUsers();
      setFirebaseUsers(fbUsers);
    } catch (error) {
      console.error('Error fetching Firebase users:', error);
      alert('Nepodařilo se načíst Firebase uživatele. Backend možná ještě nebyl nasazen.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on tab change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'analyses') {
          // TODO: Fetch from backend when admin endpoints are ready
          // For now, show mock data
          setAnalyses([
            {
              task_id: 'abc123',
              email: 'test@example.com',
              date: new Date().toISOString(),
              status: 'completed',
              election_type: 'komunalni',
              municipality: 'Praha'
            },
            {
              task_id: 'def456',
              email: 'user@example.com',
              date: new Date(Date.now() - 3600000).toISOString(),
              status: 'running',
              election_type: 'senatni',
              municipality: 'Brno-město'
            }
          ]);
        } else if (activeTab === 'users') {
          await fetchUsers();
        } else if (activeTab === 'config') {
          // TODO: Fetch from backend
          setConfig({
            admin_emails: ['pecka@dwx.cz'],
            email_enabled: true,
            version: '6.4'
          });
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab]);

  // Handle approve user
  const handleApproveUser = async (uid: string) => {
    if (!user?.email) return;
    setActionLoading(uid);
    try {
      // Find the user to get their name and email
      const targetUser = users.find(u => u.uid === uid);

      await approveUser(uid, user.email);

      // Send approval notification email (fire and forget)
      if (targetUser) {
        const fullName = `${targetUser.firstName} ${targetUser.lastName}`;
        notifyApproval(targetUser.email, fullName, true)
          .catch(err => console.error('Approval notification failed:', err));
      }

      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject user
  const handleRejectUser = async (uid: string) => {
    if (!user?.email) return;
    setActionLoading(uid);
    try {
      // Find the user to get their name and email
      const targetUser = users.find(u => u.uid === uid);

      await rejectUser(uid, user.email);

      // Send rejection notification email (fire and forget)
      if (targetUser) {
        const fullName = `${targetUser.firstName} ${targetUser.lastName}`;
        notifyApproval(targetUser.email, fullName, false)
          .catch(err => console.error('Rejection notification failed:', err));
      }

      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete user (deletes from both Firebase Auth AND Firestore)
  const handleDeleteUser = async (uid: string, email?: string) => {
    const targetUser = users.find(u => u.uid === uid);
    const confirmMsg = targetUser
      ? `Opravdu chcete smazat uživatele ${targetUser.firstName} ${targetUser.lastName} (${targetUser.email})?\n\nToto TRVALE smaže účet z Firebase Auth i Firestore.`
      : email
        ? `Opravdu chcete smazat uživatele ${email}?\n\nToto TRVALE smaže účet z Firebase Auth.`
        : 'Opravdu chcete smazat tohoto uživatele?\n\nToto TRVALE smaže účet.';

    if (!window.confirm(confirmMsg)) return;

    setActionLoading(uid);
    try {
      // 1. Delete from Firebase Auth (via backend API)
      if (user?.email) {
        await deleteFirebaseUser(uid, user.email);
      }

      // 2. Delete from Firestore (if exists)
      try {
        await deleteUserDocument(uid);
      } catch (err) {
        // Firestore document might not exist (old Firebase-only users)
        console.log('No Firestore document to delete (or already deleted)');
      }

      // 3. Refresh both lists
      await fetchUsers();
      if (showFirebaseUsers) {
        await fetchFirebaseUsers();
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(`Chyba při mazání uživatele: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle update credits
  const handleUpdateCredits = async (uid: string) => {
    const newCredits = parseInt(creditInputValue, 10);
    if (isNaN(newCredits) || newCredits < 0) {
      alert('Zadejte platné číslo (0 nebo více)');
      return;
    }
    setActionLoading(uid);
    try {
      await updateUserCredits(uid, newCredits);
      await fetchUsers(); // Refresh the list
      setEditingCredits(null);
      setCreditInputValue('');
    } catch (error) {
      console.error('Error updating credits:', error);
      alert('Chyba při aktualizaci kreditů');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      completed: {
        bg: 'var(--success-100)',
        text: 'var(--success-600)',
        icon: <CheckIcon />
      },
      running: {
        bg: 'var(--warning-100)',
        text: 'var(--warning-600)',
        icon: <ClockIcon />
      },
      pending: {
        bg: 'var(--phoenix-100)',
        text: 'var(--phoenix-600)',
        icon: <ClockIcon />
      },
      failed: {
        bg: 'var(--danger-100)',
        text: 'var(--danger-600)',
        icon: <XIcon />
      }
    };

    const style = styles[status] || styles.pending;

    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
        style={{ background: style.bg, color: style.text }}
      >
        {style.icon}
        {status}
      </span>
    );
  };

  const tabs = [
    { id: 'analyses' as TabType, label: 'Analýzy', icon: <ChartIcon /> },
    { id: 'users' as TabType, label: 'Uživatelé', icon: <UsersIcon /> },
    { id: 'config' as TabType, label: 'Konfigurace', icon: <SettingsIcon /> }
  ];

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Link to="/filter" className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                  style={{
                    background: 'linear-gradient(135deg, var(--phoenix-400) 0%, var(--phoenix-500) 100%)',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                  }}
                >
                  <LogoIcon />
                </div>
                <div>
                  <h1 className="font-display text-lg font-semibold text-white tracking-tight">
                    Phoenix Analytics
                  </h1>
                  <p className="text-xs text-white/60">Admin Panel</p>
                </div>
              </Link>
            </div>

            {/* Back to Filter */}
            <div className="flex items-center gap-4">
              <Link
                to="/filter"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all text-white/80 hover:text-white hover:bg-white/10"
              >
                <ArrowLeftIcon />
                Zpět na analýzu
              </Link>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-white/10"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                    style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                  >
                    <ShieldIcon />
                  </div>
                  <span className="hidden sm:block text-sm text-white/90 max-w-[150px] truncate">
                    {user?.email}
                  </span>
                </button>

                {showUserMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden"
                    style={{
                      background: 'var(--bg-secondary)',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        Admin
                      </p>
                      <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
                        {user?.email}
                      </p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-red-50"
                        style={{ color: 'var(--danger-600)' }}
                      >
                        <LogoutIcon />
                        Odhlásit se
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2
              className="font-display text-2xl lg:text-3xl font-semibold mb-2 flex items-center gap-3"
              style={{ color: 'var(--text-primary)' }}
            >
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
              >
                <ShieldIcon />
              </span>
              Admin Panel
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Správa analýz, uživatelů a konfigurace systému
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-light)'
            }}
          >
            <RefreshIcon />
            Obnovit
          </button>
        </div>

        {/* Tabs */}
        <div
          className="rounded-2xl mb-6"
          style={{
            background: 'var(--bg-secondary)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="flex border-b" style={{ borderColor: 'var(--border-light)' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'border-b-2'
                    : 'hover:bg-gray-50'
                }`}
                style={{
                  color: activeTab === tab.id ? 'var(--phoenix-600)' : 'var(--text-secondary)',
                  borderColor: activeTab === tab.id ? 'var(--phoenix-500)' : 'transparent'
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="w-8 h-8 border-2 rounded-full animate-spin"
                  style={{
                    borderColor: 'var(--phoenix-200)',
                    borderTopColor: 'var(--phoenix-500)'
                  }}
                />
              </div>
            ) : (
              <>
                {/* Analyses Tab */}
                {activeTab === 'analyses' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Přehled analýz
                    </h3>
                    {analyses.length === 0 ? (
                      <p style={{ color: 'var(--text-secondary)' }}>Žádné analýzy k zobrazení.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Task ID
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Email
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Typ
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Obec
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Datum
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Stav
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyses.map((analysis) => (
                              <tr
                                key={analysis.task_id}
                                style={{ borderBottom: '1px solid var(--border-light)' }}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="py-3 px-4">
                                  <code
                                    className="text-xs px-2 py-1 rounded"
                                    style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                                  >
                                    {analysis.task_id}
                                  </code>
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {analysis.email}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {analysis.election_type}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {analysis.municipality}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {new Date(analysis.date).toLocaleString('cs-CZ')}
                                </td>
                                <td className="py-3 px-4">
                                  {getStatusBadge(analysis.status)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Správa uživatelů
                    </h3>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div
                        className="p-4 rounded-xl"
                        style={{ background: 'var(--bg-tertiary)' }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span style={{ color: 'var(--phoenix-500)' }}><UsersIcon /></span>
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Celkem uživatelů
                          </span>
                        </div>
                        <p className="text-2xl font-semibold" style={{ color: 'var(--phoenix-600)' }}>
                          {users.length}
                        </p>
                      </div>
                      <div
                        className="p-4 rounded-xl"
                        style={{ background: 'var(--warning-50)', border: '1px solid var(--warning-200)' }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span style={{ color: 'var(--warning-500)' }}><ClockIcon /></span>
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Čeká na schválení
                          </span>
                        </div>
                        <p className="text-2xl font-semibold" style={{ color: 'var(--warning-600)' }}>
                          {pendingUsers.length}
                        </p>
                      </div>
                      <div
                        className="p-4 rounded-xl"
                        style={{ background: 'var(--success-50)', border: '1px solid var(--success-200)' }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span style={{ color: 'var(--success-500)' }}><CheckIcon /></span>
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Schválených
                          </span>
                        </div>
                        <p className="text-2xl font-semibold" style={{ color: 'var(--success-600)' }}>
                          {users.filter(u => u.status === 'approved').length}
                        </p>
                      </div>
                    </div>

                    {/* Email Spam Warning */}
                    <div
                      className="p-4 rounded-xl mb-6 flex items-start gap-3"
                      style={{ background: 'var(--phoenix-50)', border: '1px solid var(--phoenix-200)' }}
                    >
                      <span style={{ color: 'var(--phoenix-500)', marginTop: '2px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      </span>
                      <div>
                        <p className="font-medium text-sm" style={{ color: 'var(--phoenix-700)' }}>
                          Upozornění na spam
                        </p>
                        <p className="text-sm" style={{ color: 'var(--phoenix-600)' }}>
                          Emailové notifikace o nových registracích a schváleních mohou být ve složce <strong>SPAM</strong>.
                          Zkontrolujte spam složku, pokud nedostáváte upozornění.
                        </p>
                      </div>
                    </div>

                    {/* Pending Users Section */}
                    {pendingUsers.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-md font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--warning-600)' }}>
                          <ClockIcon />
                          Čekající na schválení ({pendingUsers.length})
                        </h4>
                        <div className="space-y-3">
                          {pendingUsers.map((pendingUser) => (
                            <div
                              key={pendingUser.uid}
                              className="p-4 rounded-xl flex items-center justify-between"
                              style={{ background: 'var(--warning-50)', border: '1px solid var(--warning-200)' }}
                            >
                              <div>
                                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                  {pendingUser.firstName} {pendingUser.lastName}
                                </p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {pendingUser.email}
                                </p>
                                {pendingUser.localAssociation && (
                                  <p className="text-sm mt-1" style={{ color: 'var(--phoenix-600)' }}>
                                    <strong>MS:</strong> {pendingUser.localAssociation}
                                  </p>
                                )}
                                <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                                  Registrován: {pendingUser.createdAt?.toDate?.()?.toLocaleString('cs-CZ') || 'N/A'}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleApproveUser(pendingUser.uid)}
                                  disabled={actionLoading === pendingUser.uid}
                                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                                  style={{
                                    background: 'var(--success-500)',
                                    color: 'white',
                                    opacity: actionLoading === pendingUser.uid ? 0.7 : 1
                                  }}
                                >
                                  {actionLoading === pendingUser.uid ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <CheckIcon />
                                  )}
                                  Schválit
                                </button>
                                <button
                                  onClick={() => handleRejectUser(pendingUser.uid)}
                                  disabled={actionLoading === pendingUser.uid}
                                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                                  style={{
                                    background: 'var(--danger-500)',
                                    color: 'white',
                                    opacity: actionLoading === pendingUser.uid ? 0.7 : 1
                                  }}
                                >
                                  <XIcon />
                                  Zamítnout
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Users Table */}
                    <h4 className="text-md font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                      Všichni uživatelé
                    </h4>
                    {users.length === 0 ? (
                      <p style={{ color: 'var(--text-secondary)' }}>Žádní uživatelé k zobrazení.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Jméno
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Email
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Místní sdružení
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Stav
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Kredity
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Registrován
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                Akce
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((u) => (
                              <tr
                                key={u.uid}
                                style={{ borderBottom: '1px solid var(--border-light)' }}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {u.firstName} {u.lastName}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                  {u.email}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: u.localAssociation ? 'var(--phoenix-600)' : 'var(--text-tertiary)' }}>
                                  {u.localAssociation || '—'}
                                </td>
                                <td className="py-3 px-4">
                                  {getStatusBadge(u.status)}
                                </td>
                                <td className="py-3 px-4">
                                  {editingCredits === u.uid ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        value={creditInputValue}
                                        onChange={(e) => setCreditInputValue(e.target.value)}
                                        className="w-16 px-2 py-1 text-sm rounded border"
                                        style={{ borderColor: 'var(--border-light)' }}
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleUpdateCredits(u.uid);
                                          if (e.key === 'Escape') setEditingCredits(null);
                                        }}
                                      />
                                      <button
                                        onClick={() => handleUpdateCredits(u.uid)}
                                        disabled={actionLoading === u.uid}
                                        className="p-1 rounded"
                                        style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                                      >
                                        <CheckIcon />
                                      </button>
                                      <button
                                        onClick={() => setEditingCredits(null)}
                                        className="p-1 rounded"
                                        style={{ background: 'var(--danger-100)', color: 'var(--danger-600)' }}
                                      >
                                        <XIcon />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditingCredits(u.uid);
                                        setCreditInputValue(String(u.credits ?? 0));
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium hover:opacity-80 transition-opacity"
                                      style={{
                                        background: (u.credits ?? 0) > 0 ? 'var(--success-100)' : 'var(--danger-100)',
                                        color: (u.credits ?? 0) > 0 ? 'var(--success-600)' : 'var(--danger-600)'
                                      }}
                                      title="Kliknutím upravíte kredity"
                                    >
                                      <CoinIcon />
                                      {u.credits ?? 0}
                                    </button>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {u.createdAt?.toDate?.()?.toLocaleDateString('cs-CZ') || 'N/A'}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex gap-2">
                                    {u.status === 'pending' && (
                                      <>
                                        <button
                                          onClick={() => handleApproveUser(u.uid)}
                                          disabled={actionLoading === u.uid}
                                          className="p-2 rounded-lg transition-all"
                                          style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
                                          title="Schválit"
                                        >
                                          <CheckIcon />
                                        </button>
                                        <button
                                          onClick={() => handleRejectUser(u.uid)}
                                          disabled={actionLoading === u.uid}
                                          className="p-2 rounded-lg transition-all"
                                          style={{ background: 'var(--danger-100)', color: 'var(--danger-600)' }}
                                          title="Zamítnout"
                                        >
                                          <XIcon />
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => handleDeleteUser(u.uid)}
                                      disabled={actionLoading === u.uid}
                                      className="p-2 rounded-lg transition-all hover:bg-red-200"
                                      style={{ background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}
                                      title="Smazat uživatele"
                                    >
                                      <TrashIcon />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Firebase Auth Users Section */}
                    <div className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                          <ShieldIcon />
                          Firebase Auth uživatelé (přímý přístup)
                        </h4>
                        <button
                          onClick={() => {
                            setShowFirebaseUsers(!showFirebaseUsers);
                            if (!showFirebaseUsers && firebaseUsers.length === 0) {
                              fetchFirebaseUsers();
                            }
                          }}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                          style={{
                            background: showFirebaseUsers ? 'var(--phoenix-500)' : 'var(--bg-tertiary)',
                            color: showFirebaseUsers ? 'white' : 'var(--text-primary)',
                            border: '1px solid var(--border-light)'
                          }}
                        >
                          {showFirebaseUsers ? 'Skrýt' : 'Zobrazit všechny Firebase uživatele'}
                        </button>
                      </div>

                      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                        Tato sekce zobrazuje VŠECHNY uživatele přímo z Firebase Authentication.
                        Zahrnuje i staré uživatele, kteří nemají dokument ve Firestore.
                      </p>

                      {showFirebaseUsers && (
                        <>
                          {loading ? (
                            <div className="flex items-center justify-center py-8">
                              <div
                                className="w-6 h-6 border-2 rounded-full animate-spin"
                                style={{
                                  borderColor: 'var(--phoenix-200)',
                                  borderTopColor: 'var(--phoenix-500)'
                                }}
                              />
                            </div>
                          ) : firebaseUsers.length === 0 ? (
                            <p style={{ color: 'var(--text-secondary)' }}>
                              Žádní Firebase uživatelé nenalezeni.
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Email
                                    </th>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Ověřený
                                    </th>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Vytvořen
                                    </th>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Poslední přihlášení
                                    </th>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Ve Firestore
                                    </th>
                                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                      Akce
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {firebaseUsers.map((fbUser) => {
                                    const hasFirestoreDoc = users.some(u => u.uid === fbUser.uid);
                                    return (
                                      <tr
                                        key={fbUser.uid}
                                        style={{ borderBottom: '1px solid var(--border-light)' }}
                                        className="hover:bg-gray-50 transition-colors"
                                      >
                                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                                          {fbUser.email}
                                          {fbUser.display_name && (
                                            <span className="text-xs ml-2" style={{ color: 'var(--text-tertiary)' }}>
                                              ({fbUser.display_name})
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-3 px-4">
                                          {fbUser.email_verified ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                              style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}>
                                              <CheckIcon /> Ano
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                              style={{ background: 'var(--warning-100)', color: 'var(--warning-600)' }}>
                                              <XIcon /> Ne
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                          {fbUser.created_at ? new Date(parseInt(fbUser.created_at)).toLocaleDateString('cs-CZ') : 'N/A'}
                                        </td>
                                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                          {fbUser.last_sign_in ? new Date(parseInt(fbUser.last_sign_in)).toLocaleDateString('cs-CZ') : 'N/A'}
                                        </td>
                                        <td className="py-3 px-4">
                                          {hasFirestoreDoc ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                              style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}>
                                              <CheckIcon /> Ano
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                              style={{ background: 'var(--danger-100)', color: 'var(--danger-600)' }}>
                                              <XIcon /> Ne (starý)
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-3 px-4">
                                          <button
                                            onClick={() => handleDeleteUser(fbUser.uid, fbUser.email)}
                                            disabled={actionLoading === fbUser.uid}
                                            className="p-2 rounded-lg transition-all hover:bg-red-200"
                                            style={{ background: 'var(--danger-100)', color: 'var(--danger-600)' }}
                                            title="Smazat uživatele z Firebase Auth"
                                          >
                                            {actionLoading === fbUser.uid ? (
                                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                              <TrashIcon />
                                            )}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <p className="mt-4 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                Celkem {firebaseUsers.length} uživatelů ve Firebase Auth |
                                {firebaseUsers.filter(fb => users.some(u => u.uid === fb.uid)).length} má Firestore dokument |
                                {firebaseUsers.filter(fb => !users.some(u => u.uid === fb.uid)).length} starých (bez Firestore)
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Config Tab */}
                {activeTab === 'config' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Konfigurace systému
                    </h3>
                    {config && (
                      <div className="space-y-4">
                        <div
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--bg-tertiary)' }}
                        >
                          <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                            Verze systému
                          </h4>
                          <p className="text-lg font-semibold" style={{ color: 'var(--phoenix-600)' }}>
                            Phoenix Analytics v{config.version}
                          </p>
                        </div>
                        <div
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--bg-tertiary)' }}
                        >
                          <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                            Email notifikace
                          </h4>
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ background: config.email_enabled ? 'var(--success-500)' : 'var(--danger-500)' }}
                            />
                            <span style={{ color: 'var(--text-secondary)' }}>
                              {config.email_enabled ? 'Aktivní' : 'Neaktivní'}
                            </span>
                          </div>
                        </div>
                        <div
                          className="p-4 rounded-xl"
                          style={{ background: 'var(--bg-tertiary)' }}
                        >
                          <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                            Admin emaily
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {config.admin_emails.map((email) => (
                              <span
                                key={email}
                                className="px-3 py-1 rounded-full text-sm"
                                style={{
                                  background: 'var(--phoenix-100)',
                                  color: 'var(--phoenix-700)'
                                }}
                              >
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div
                          className="p-4 rounded-xl"
                          style={{
                            background: 'var(--warning-50)',
                            border: '1px solid var(--warning-200)'
                          }}
                        >
                          <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--warning-700)' }}>
                            Upozornění
                          </h4>
                          <p className="text-sm" style={{ color: 'var(--warning-600)' }}>
                            Změna konfigurace vyžaduje úpravu environment variables v Cloud Run a redeployment.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </div>
  );
};

export default AdminPage;
